import {Mapper} from '@s-ui/domain'

export class SeoAdsListSubscriptionMetaInformationMapper extends Mapper {
  constructor({
    seoDataValueObjectFactory,
    seoDataMapper,
    seoLiteralMapper,
    breadcrumbListStructuredDataSeoMapper,
    webPageStructuredDataSeoMapper,
    vehicleStructuredDataSeoMapper,
    config
  }) {
    super()

    this._seoDataValueObjectFactory = seoDataValueObjectFactory
    this._seoDataMapper = seoDataMapper
    this._seoLiteralMapper = seoLiteralMapper
    this._vehicleStructuredDataSeoMapper = vehicleStructuredDataSeoMapper
    this._breadcrumbListStructuredDataSeoMapper = breadcrumbListStructuredDataSeoMapper
    this._webPageStructuredDataSeoMapper = webPageStructuredDataSeoMapper
    this._config = config
    this._seoConfig = this._config.get('seo')
    this._seoRentingHardcodedSchemaData = this._seoConfig.SEO_RENTING_HARDCODED_SCHEMA_DATA
    this._vehicleStateIdSlugMapping = this._config.get('page').VEHICLE_STATE_ID_SLUG_MAPPING
    this._adsListSubcriptionPathPattern = this._config.get('page').ADS_LIST_SUBSCRIPTION_PATH_PATTERN
    this._vehicleStateIds = this._config.get('subscription').VEHICLE_STATE_IDS
    this._SPACE = ' '
  }

  _getVehicleStateIdFromSlug = ({vehicleState}) => {
    for (const [key, value] of Object.entries(this._vehicleStateIdSlugMapping)) {
      if (value === vehicleState) return key
    }
  }

  _getVehicleStateLiteral = ({subscriptionVehicleState}) => {
    const VEHICLE_STATE_LITERAL_MAPPER = {
      [this._vehicleStateIds.NEW]: 'nuevos',
      [this._vehicleStateIds.USED]: 'de segunda mano y seminuevos'
    }
    return VEHICLE_STATE_LITERAL_MAPPER[subscriptionVehicleState]
  }

  _getTargetBuyerLiteral = ({targetBuyer, sortBy, sortOrder}) => {
    if (targetBuyer) {
      return targetBuyer === 'privates' ? 'particulares' : 'empresas'
    }

    if (sortBy === 'year' && sortOrder === 'DESC') {
      return 'particulares'
    }
  }

  _getMeta = metaParams => {
    const {
      SEMANTIC_KEYWORDS,
      subscriptionAdsList,
      subscriptionAdsListBodyTypeMeta,
      subscriptionAdsListFuelTypeMeta,
      subscriptionAdsListFuelTypeMakeMeta,
      subscriptionAdsListMakeMeta,
      subscriptionAdsListMakeModelMeta,
      subscriptionAdsListVehicleStateMeta,
      subscriptionAdsListSemanticTargetBuyerMeta,
      subscriptionAdsListSemanticKeywordMeta,
      subscriptionAdsListSemanticCommitmentMonthsMeta,
      subscriptionAdsListSemanticKeywordMakeMeta,
      subscriptionAdsListSemanticVehicleVanTypeMeta,
      subscriptionAdsListSemanticTargetBuyerMakeMeta
    } = this._config.get('seo')

    const {bodyType, canonical, fuelType, make, model, targetBuyer, vehicleState} = metaParams

    const semanticKeys = canonical?.split('/renting')?.[1]?.split('/').filter(Boolean) || []

    if (make && fuelType) {
      return subscriptionAdsListFuelTypeMakeMeta
    }

    if (make && targetBuyer) {
      return subscriptionAdsListSemanticTargetBuyerMakeMeta[targetBuyer]
    }

    if (make && vehicleState) {
      return subscriptionAdsListVehicleStateMeta
    }

    if (make && bodyType) {
      return subscriptionAdsListBodyTypeMeta
    }
    if (make && model) {
      return subscriptionAdsListMakeModelMeta
    }

    if (semanticKeys.includes(SEMANTIC_KEYWORDS.RENTING.VEHICLE_STATE_USED)) {
      return subscriptionAdsListVehicleStateMeta
    }

    if (semanticKeys.includes(SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS)) {
      return subscriptionAdsListSemanticKeywordMeta[SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS]
    }

    if (semanticKeys.includes(SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_NONE)) {
      return subscriptionAdsListSemanticKeywordMeta[SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_NONE]
    }

    if (
      semanticKeys.some(key =>
        [
          SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_12,
          SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_24,
          SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_36,
          SEMANTIC_KEYWORDS.RENTING.COMMITMENT_MONTHS_48
        ].includes(key)
      )
    ) {
      return subscriptionAdsListSemanticCommitmentMonthsMeta
    }

    if (semanticKeys.includes(SEMANTIC_KEYWORDS.RENTING.NO_ENTRY_FEE)) {
      return make
        ? subscriptionAdsListSemanticKeywordMakeMeta[SEMANTIC_KEYWORDS.RENTING.NO_ENTRY_FEE]
        : subscriptionAdsListSemanticKeywordMeta[SEMANTIC_KEYWORDS.RENTING.NO_ENTRY_FEE]
    }

    if (semanticKeys.includes(SEMANTIC_KEYWORDS.RENTING.CHEAP)) {
      return subscriptionAdsListSemanticKeywordMeta[SEMANTIC_KEYWORDS.RENTING.CHEAP]
    }

    if (semanticKeys.includes(SEMANTIC_KEYWORDS.RENTING.TARGET_BUYER_PRIVATE)) {
      return make
        ? subscriptionAdsListSemanticTargetBuyerMakeMeta[SEMANTIC_KEYWORDS.RENTING.TARGET_BUYER_PRIVATE]
        : subscriptionAdsListSemanticTargetBuyerMeta[SEMANTIC_KEYWORDS.RENTING.TARGET_BUYER_PRIVATE]
    }

    if (semanticKeys.includes(SEMANTIC_KEYWORDS.RENTING.TARGET_BUYER_BUSINESS)) {
      return make
        ? subscriptionAdsListSemanticTargetBuyerMakeMeta[SEMANTIC_KEYWORDS.RENTING.TARGET_BUYER_BUSINESS]
        : subscriptionAdsListSemanticTargetBuyerMeta[SEMANTIC_KEYWORDS.RENTING.TARGET_BUYER_BUSINESS]
    }

    if (semanticKeys.includes(SEMANTIC_KEYWORDS.RENTING.CHEAP)) {
      return subscriptionAdsListSemanticKeywordMeta[SEMANTIC_KEYWORDS.RENTING.CHEAP]
    }

    if (semanticKeys.includes(SEMANTIC_KEYWORDS.RENTING.NO_COMMITMENT)) {
      return subscriptionAdsListSemanticKeywordMeta[SEMANTIC_KEYWORDS.RENTING.NO_COMMITMENT]
    }

    if (semanticKeys.includes(SEMANTIC_KEYWORDS.RENTING.VEHICLE_TYPE_VAN)) {
      return subscriptionAdsListSemanticVehicleVanTypeMeta
    }

    if (bodyType) {
      return subscriptionAdsListBodyTypeMeta
    }

    if (fuelType) {
      return subscriptionAdsListFuelTypeMeta
    }

    if (make) {
      return subscriptionAdsListMakeMeta
    }

    if (vehicleState) {
      return subscriptionAdsListVehicleStateMeta
    }

    return subscriptionAdsList
  }

  /**
   * @method
   */
  async map({culture, params = {}}) {
    const {CRAWLABLE_AND_INDEXABLE_META_ROBOTS, NO_CRAWLABLE_NOR_INDEXABLE_META_ROBOTS} = this._seoConfig

    const isMainRentingPage = params?.path === this._adsListSubcriptionPathPattern

    const {
      bodyType,
      breadcrumb,
      canonical,
      commitmentMonths,
      fuelType,
      keywordSlug,
      make,
      minPrice,
      maxPrice,
      model,
      nResults,
      sortBy,
      sortOrder,
      subscriptionVehicleState,
      targetBuyer,
      vehicleType
    } = params

    const vehicleState = this._getVehicleStateLiteral({subscriptionVehicleState})

    const subscriptionAdsListMeta = this._getMeta({
      bodyType,
      canonical,
      commitmentMonths,
      culture,
      fuelType,
      subscriptionVehicleState,
      keywordSlug,
      make,
      model,
      sortBy,
      sortOrder,
      targetBuyer,
      vehicleState,
      vehicleType
    })

    const {TITLE, H1, META_DESCRIPTION, META_ROBOTS} = subscriptionAdsListMeta[culture]

    const targetBuyerLiteral = this._getTargetBuyerLiteral({sortBy, sortOrder, targetBuyer})

    const placeholderValues = {
      bodyType,
      commitmentMonths,
      fuelType,
      make,
      space: make ? this._SPACE : '',
      minPrice,
      model,
      nResults,
      targetBuyer: targetBuyerLiteral,
      vehicleType,
      vehicleState
    }

    const parsedTitle = this._seoLiteralMapper.setText({text: TITLE}).setPlaceholderValues({placeholderValues}).map()

    const parsedH1 = this._seoLiteralMapper.setText({text: H1}).setPlaceholderValues({placeholderValues}).map()

    const hasBreadcrumb = breadcrumb && breadcrumb.length > 0

    const parsedDescription = this._seoLiteralMapper
      .setText({text: META_DESCRIPTION})
      .setPlaceholderValues({placeholderValues})
      .map()

    const parsedMetaRobots = this._seoLiteralMapper
      .setText({text: META_ROBOTS})
      .setPlaceholderValues({
        placeholderValues: {
          metaRobots: nResults ? CRAWLABLE_AND_INDEXABLE_META_ROBOTS : NO_CRAWLABLE_NOR_INDEXABLE_META_ROBOTS
        }
      })
      .map()

    const webPageStructuredDataVO = this._webPageStructuredDataSeoMapper.map({
      canonical,
      name: parsedTitle
    })

    const breadcrumbStructuredDataVO =
      hasBreadcrumb &&
      this._breadcrumbListStructuredDataSeoMapper.map({
        breadcrumb,
        id: `${canonical}#breadcrumb`
      })

    const vehicleStructuredDataVO = this._vehicleStructuredDataSeoMapper.map({
      name: parsedTitle,
      canonical,
      make,
      model,
      lowPrice: minPrice,
      highPrice: maxPrice?.value,
      nResults,
      fuelType,
      bodyType,
      offerTypeId: subscriptionVehicleState || this._config.get('search').OFFER_TYPE_SUBSCRIPTION
    })

    const {links, metas, scripts, title} = this._seoDataMapper
      .title(parsedTitle)
      .description(parsedDescription)
      .canonical(canonical)
      .robots(parsedMetaRobots)
      .addSchemaData(webPageStructuredDataVO)
      .addSchemaData(breadcrumbStructuredDataVO)
      .addSchemaData(vehicleStructuredDataVO)
      .addSchemaData(isMainRentingPage && this._seoRentingHardcodedSchemaData)
      .useGraphFormat()
      .setOpenGraphData({
        title: parsedTitle,
        description: parsedDescription,
        url: canonical
      })
      .setTwitterCardsData()
      .map()

    return this._seoDataValueObjectFactory({
      h1: parsedH1,
      head: {
        link: links,
        meta: metas,
        scripts,
        title
      }
    })
  }
}
